import React, { useState, useEffect } from 'react';
import { 
  Building2, Users, Activity, MessageSquare, Plus, ChevronRight, Clock,
  MessageCircle, ThumbsUp, Check, X, AlertTriangle, BarChart3, UserPlus, Globe,
  Loader, RefreshCw, Sparkles
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import CreateOrganizationModal from './CreateOrganizationModal';
import OrganizationDashboard from './OrganizationDashboard';
import UserManagement from './UserManagement';
import BrandsManagement from './BrandsManagement';
import SimpleBrandsList from './SimpleBrandsList';
import { clientLogger } from '../utils/logger';
import { authenticatedFetch, syncUserSession } from '../utils/apiUtils';
import { toast } from 'react-hot-toast';

const AdminDashboard = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [organizations, setOrganizations] = useState([]);
    const [users, setUsers] = useState([]);
    const [recentActivity, setRecentActivity] = useState([]);
    const [metrics, setMetrics] = useState({
        totalOrgs: 0,
        totalUsers: 0,
        activeUsers: 0,
        totalResponses: 0,
        totalComments: 0
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const [success, setSuccess] = useState(null);
    const [authChecked, setAuthChecked] = useState(false);

    // Initial authentication check
    useEffect(() => {
        // Set a global timeout to ensure loading state doesn't get stuck
        const globalTimeout = setTimeout(() => {
            if (loading) {
                clientLogger.warn('AdminDashboard: Global timeout reached, forcing loading state to false');
                
                // Check if we have any data loaded before showing the timeout notification
                const hasOrganizations = organizations && organizations.length > 0;
                const hasUsers = users && users.length > 0;
                const hasActivity = recentActivity && recentActivity.length > 0;
                
                // Only show timeout notification if we have no data at all
                if (!hasOrganizations && !hasUsers && !hasActivity) {
                    // Show toast notification instead of setting error state
                    toast.error('Dashboard loading timed out. Some data may not be available.', {
                        duration: 5000,
                        position: 'top-right',
                        icon: '⚠️',
                        style: {
                            borderRadius: '10px',
                            background: '#FEF2F2',
                            color: '#B91C1C',
                        },
                    });
                } else {
                    // We have some data, so just silently end loading state without error
                    clientLogger.info('AdminDashboard: Some data loaded, continuing without error');
                }
                
                // Always set loading to false and clear error state
                setLoading(false);
                setError(null);
            }
        }, 15000); // Increased to 15 seconds for more reliable loading
        
        const checkAuthentication = async () => {
            clientLogger.debug('AdminDashboard: Checking admin authentication...');
            
            try {
                // Get user data from localStorage
                const userDataString = localStorage.getItem('userData');
                const userData = userDataString ? JSON.parse(userDataString) : null;
                
                if (!userData) {
                    clientLogger.warn('AdminDashboard: No user data in localStorage');
                    setError('Authentication required');
                    setAuthChecked(true);
                    setLoading(false);
                    return;
                }
                
                // Handle potential nested structure (old format)
                const userToCheck = userData.user || userData;
                
                // Check if user is an admin
                if (!userToCheck.is_admin) {
                    clientLogger.warn('AdminDashboard: User is not an admin', { userData: userToCheck });
                    setError('Admin privileges required');
                    setAuthChecked(true);
                    setLoading(false);
                    return;
                }
                
                // Try syncing the session to ensure server-side auth
                const sessionSynced = await syncUserSession();
                
                if (!sessionSynced) {
                    clientLogger.warn('AdminDashboard: Session sync failed, still proceeding with dashboard load');
                    setAuthChecked(true);
                    // Continue to fetchDashboardData even if session sync fails - it can retry there
                }
                
                clientLogger.debug('AdminDashboard: Admin authentication successful');
                setAuthChecked(true);
                // Fetch dashboard data
                fetchDashboardData();
            } catch (err) {
                clientLogger.error('AdminDashboard: Error during authentication check', err);
                setError('Error checking authentication: ' + err.message);
                setLoading(false);
            }
        };
        
        checkAuthentication();
        
        // Clean up the timeout
        return () => clearTimeout(globalTimeout);
    }, []);

    const fetchDashboardData = async (retryCount = 0) => {
        try {
            clientLogger.debug('AdminDashboard: Fetching dashboard data...', { retryCount });
            
            // Add this line to sync the session before making API calls
            const sessionSynced = await syncUserSession();
            clientLogger.debug('AdminDashboard: Session sync result:', { sessionSynced });
            
            if (!sessionSynced && retryCount === 0) {
                clientLogger.warn('AdminDashboard: Session sync failed, retrying once...');
                // Wait a moment and try again
                setTimeout(() => fetchDashboardData(retryCount + 1), 1000);
                return;
            }
            
            // Display a loading message instead of blocking UI
            if (retryCount === 0) {
                clientLogger.debug('AdminDashboard: Making API requests...');
            }
            
            // Get user data to include in requests
            const userDataString = localStorage.getItem('userData');
            const userData = userDataString ? JSON.parse(userDataString) : null;
            
            if (!userData || !userData.id) {
                clientLogger.error('AdminDashboard: No valid user data available for API requests');
                setLoading(false);
                setError('User data not available. Please log in again.');
                return;
            }
            
            // Add userId to URLs
            const userId = userData.id;
            clientLogger.debug('AdminDashboard: Using user ID for API requests', { userId });
            
            // Add timeout to prevent hanging requests
            const controller = new AbortController();
            const timeoutId = setTimeout(() => {
                clientLogger.warn('AdminDashboard: API request timeout reached, aborting');
                controller.abort();
            }, 10000); // Increase timeout to 10 seconds
            
            // Create promises for all API calls
            const organizationsPromise = authenticatedFetch(`/api/admin/organizations?userId=${userId}`, { 
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache',
                    'X-User-ID': userId
                },
                signal: controller.signal
            }).catch(err => {
                clientLogger.error('AdminDashboard: Error fetching organizations:', err);
                return { ok: false, error: err };
            });
            
            const usersPromise = authenticatedFetch(`/api/admin/users?userId=${userId}`, { 
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache',
                    'X-User-ID': userId
                },
                signal: controller.signal
            }).catch(err => {
                clientLogger.error('AdminDashboard: Error fetching users:', err);
                return { ok: false, error: err };
            });
            
            const activityPromise = authenticatedFetch(`/api/admin/recent-activity?userId=${userId}`, { 
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache',
                    'X-User-ID': userId
                },
                signal: controller.signal
            }).catch(err => {
                clientLogger.error('AdminDashboard: Error fetching activity:', err);
                return { ok: false, error: err };
            });
            
            const metricsPromise = authenticatedFetch(`/api/admin/metrics?userId=${userId}`, { 
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache',
                    'X-User-ID': userId
                },
                signal: controller.signal
            }).catch(err => {
                clientLogger.error('AdminDashboard: Error fetching metrics:', err);
                return { ok: false, error: err };
            });
            
            clientLogger.debug('AdminDashboard: Starting API requests');
            
            // Wait for all promises to resolve, regardless of success/failure
            const [orgsRes, usersRes, activityRes, metricsRes] = await Promise.all([
                organizationsPromise, 
                usersPromise, 
                activityPromise, 
                metricsPromise
            ]);
            
            // Clear the timeout
            clearTimeout(timeoutId);
            
            // Process successful responses and skip failed ones
            const results = [];
            
            // Process organizations
            if (orgsRes.ok) {
                try {
                    const orgsData = await orgsRes.json();
                    setOrganizations(orgsData);
                    results.push('organizations');
                } catch (err) {
                    clientLogger.error('AdminDashboard: Error parsing organizations response', err);
                }
            }
            
            // Process users
            if (usersRes.ok) {
                try {
                    const usersData = await usersRes.json();
                    setUsers(usersData);
                    results.push('users');
                } catch (err) {
                    clientLogger.error('AdminDashboard: Error parsing users response', err);
                }
            }
            
            // Process activity
            if (activityRes.ok) {
                try {
                    const activityData = await activityRes.json();
                    setRecentActivity(activityData);
                    results.push('activity');
                } catch (err) {
                    clientLogger.error('AdminDashboard: Error parsing activity response', err);
                }
            }
            
            // Process metrics
            if (metricsRes.ok) {
                try {
                    const metricsData = await metricsRes.json();
                    setMetrics(metricsData);
                    results.push('metrics');
                } catch (err) {
                    clientLogger.error('AdminDashboard: Error parsing metrics response', err);
                }
            }
            
            clientLogger.debug('AdminDashboard: Processed API responses', { 
                successful: results.join(', '),
                failed: [
                    !orgsRes.ok ? 'organizations' : null,
                    !usersRes.ok ? 'users' : null,
                    !activityRes.ok ? 'activity' : null,
                    !metricsRes.ok ? 'metrics' : null
                ].filter(Boolean).join(', ')
            });
            
            // Show partial data with warning if some requests failed
            if (results.length > 0 && results.length < 4) {
                // Only show a warning in the console, don't display an error to the user
                // as long as we have some data to show
                clientLogger.warn('AdminDashboard: Partial data loaded', { 
                    loaded: results.join(', ') 
                });
                
                // Show a toast notification instead of setting error state
                toast.warning('Some dashboard data could not be loaded. Partial information is shown.', {
                    duration: 4000,
                    position: 'top-right',
                    icon: '⚠️',
                });
                
                // Clear any existing error
                setError(null);
            } else if (results.length === 0) {
                // Show a toast notification for complete failure
                toast.error('Failed to load dashboard data. Please try again.', {
                    duration: 5000,
                    position: 'top-right',
                    icon: '❌',
                });
                
                // Still set error state for complete failure to show a retry button
                setError('Failed to load any dashboard data. Please try again.');
            } else {
                setError(null);
            }
            
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setError('Failed to load dashboard data');
        } finally {
            // Always ensure loading is set to false
            setLoading(false);
            clientLogger.debug('AdminDashboard: Finished loading dashboard data');
        }
    };

    const getActivityIcon = (type) => {
        switch (type) {
            case 'response':
                return <Check className="w-4 h-4 text-green-500" />;
            case 'comment':
                return <MessageCircle className="w-4 h-4 text-blue-500" />;
            case 'reaction':
                return <ThumbsUp className="w-4 h-4 text-purple-500" />;
            case 'admin_comment':
                return <AlertTriangle className="w-4 h-4 text-amber-500" />;
            default:
                return <Activity className="w-4 h-4 text-gray-500" />;
        }
    };

    const formatActivityDescription = (activity) => {
        const userSpan = (
            <span key="user" className="font-medium text-gray-900">
                {activity.user_name}
            </span>
        );
        
        const featureSpan = activity.feature_name && (
            <span key="feature" className="font-medium text-gray-900">
                {activity.feature_name}
            </span>
        );

        const orgSpan = (
            <span key="org" className="font-medium text-gray-900">
                {activity.organization_name}
            </span>
        );

        switch (activity.type) {
            case 'response':
                return (
                    <div className="flex items-center gap-2">
                        {userSpan} updated feature {featureSpan} to{' '}
                        <span className={`px-2 py-0.5 rounded text-xs font-medium
                            ${activity.response_value === 'required' ? 'bg-green-100 text-green-800' :
                              activity.response_value === 'nice_to_have' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'}`}>
                            {activity.response_value?.replace('_', ' ')}
                        </span>
                        {' '}for {orgSpan}
                    </div>
                );
            case 'comment':
                return (
                    <div className="flex items-center gap-2">
                        {userSpan} commented on {featureSpan} for {orgSpan}
                    </div>
                );
            case 'admin_comment':
                return (
                    <div className="flex items-center gap-2">
                        {userSpan} added admin note to {featureSpan} for {orgSpan}
                    </div>
                );
            default:
                return activity.description;
        }
    };

    const renderNavigationTabs = () => (
        <div className="mb-8 bg-white rounded-lg shadow">
            <div className="flex space-x-1 p-1">
                <button
                    onClick={() => navigate('/admin')}
                    className={`flex items-center px-4 py-2 rounded-md ${
                        location.pathname === '/admin'
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                    <BarChart3 className="w-5 h-5 mr-2" />
                    Dashboard
                </button>
                <button
                    onClick={() => navigate('/admin/users')}
                    className={`flex items-center px-4 py-2 rounded-md ${
                        location.pathname === '/admin/users'
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                    <Users className="w-5 h-5 mr-2" />
                    Users
                </button>
                <button
                    onClick={() => navigate('/admin/organizations')}
                    className={`flex items-center px-4 py-2 rounded-md ${
                        location.pathname === '/admin/organizations'
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                    <Building2 className="w-5 h-5 mr-2" />
                    Organizations
                </button>
                <button
                    onClick={() => navigate('/admin/brands')}
                    className={`flex items-center px-4 py-2 rounded-md ${
                        location.pathname === '/admin/brands'
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                    <Globe className="w-5 h-5 mr-2" />
                    Brands
                </button>
                <button
                    onClick={() => navigate('/brands-explorer')}
                    className={`flex items-center px-4 py-2 rounded-md ${
                        location.pathname === '/brands-explorer'
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                    <span className="flex items-center">
                        <Sparkles className="w-5 h-5 mr-2" />
                        Brands Explorer
                    </span>
                </button>
            </div>
        </div>
    );

    const renderMetricsSection = () => (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <Card>
                <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                        <Building2 className="w-8 h-8 text-blue-600" />
                        <div className="text-right">
                            <p className="text-2xl font-bold">{metrics.totalOrgs}</p>
                            <p className="text-sm text-gray-600">Organizations</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                        <Users className="w-8 h-8 text-green-600" />
                        <div className="text-right">
                            <p className="text-2xl font-bold">{metrics.totalUsers}</p>
                            <p className="text-sm text-gray-600">Total Users</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                        <Activity className="w-8 h-8 text-yellow-600" />
                        <div className="text-right">
                            <p className="text-2xl font-bold">{metrics.activeUsers}</p>
                            <p className="text-sm text-gray-600">Active Today</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                        <MessageSquare className="w-8 h-8 text-purple-600" />
                        <div className="text-right">
                            <p className="text-2xl font-bold">{metrics.totalComments}</p>
                            <p className="text-sm text-gray-600">Comments</p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                        <Activity className="w-8 h-8 text-red-600" />
                        <div className="text-right">
                            <p className="text-2xl font-bold">{metrics.totalResponses}</p>
                            <p className="text-sm text-gray-600">Responses</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );

    const renderOrganizationsTable = () => (
        <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Organization
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Users
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Activity
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Last Active
                            </th>
                            <th scope="col" className="relative px-6 py-3">
                                <span className="sr-only">Actions</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {organizations.map(org => (
                            <tr 
                                key={org.id}
                                onClick={() => setSelectedOrganization(org)}
                                className="hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center bg-gray-100 rounded-lg">
                                            <Building2 className="h-5 w-5 text-gray-500" />
                                        </div>
                                        <div className="ml-4">
                                            <div className="text-sm font-medium text-gray-900">
                                                {org.name}
                                            </div>
                                            {org.website_url && (
                                                <div className="text-sm text-gray-500">
                                                    {new URL(org.website_url).hostname}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center space-x-2">
                                        <Users className="h-4 w-4 text-gray-400" />
                                        <span className="text-sm text-gray-900">{org.user_count || 0}</span>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center space-x-4">
                                        <div className="flex items-center space-x-1">
                                            <MessageCircle className="h-4 w-4 text-blue-400" />
                                            <span className="text-sm text-gray-900">{org.comment_count || 0}</span>
                                        </div>
                                        <div className="flex items-center space-x-1">
                                            <Activity className="h-4 w-4 text-green-400" />
                                            <span className="text-sm text-gray-900">{org.response_count || 0}</span>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {org.last_activity ? (
                                        <div className="text-sm text-gray-900">
                                            <div className="flex items-center space-x-2">
                                                <Clock className="h-4 w-4 text-gray-400" />
                                                <span>{new Date(org.last_activity).toLocaleDateString()}</span>
                                            </div>
                                            {org.last_active_user && (
                                                <div className="text-sm text-gray-500 mt-1">
                                                    by {org.last_active_user}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="text-sm text-gray-500">No activity</span>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <ChevronRight className="h-5 w-5 text-gray-400" />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const renderOrganizationCard = (org) => (
        <Card 
            key={org.id}
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setSelectedOrganization(org)}
        >
            <CardContent className="p-6">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">{org.name}</h3>
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4 mb-4">
                            <div className="flex flex-col items-center p-3 bg-gray-50 rounded-lg">
                                <Users className="w-4 h-4 text-blue-600 mb-1" />
                                <span className="text-sm font-medium">{org.user_count || 0}</span>
                                <span className="text-xs text-gray-500">Users</span>
                            </div>
                            <div className="flex flex-col items-center p-3 bg-gray-50 rounded-lg">
                                <MessageCircle className="w-4 h-4 text-green-600 mb-1" />
                                <span className="text-sm font-medium">{org.comment_count || 0}</span>
                                <span className="text-xs text-gray-500">Comments</span>
                            </div>
                            <div className="flex flex-col items-center p-3 bg-gray-50 rounded-lg">
                                <Activity className="w-4 h-4 text-purple-600 mb-1" />
                                <span className="text-sm font-medium">{org.response_count || 0}</span>
                                <span className="text-xs text-gray-500">Responses</span>
                            </div>
                        </div>

                        {org.last_activity && (
                            <div className="flex items-center gap-2 text-sm text-gray-600 mt-4 pt-4 border-t">
                                <Clock className="w-4 h-4" />
                                <span>Last activity: {new Date(org.last_activity).toLocaleDateString()}</span>
                                {org.last_active_user && (
                                    <span className="text-gray-500">by {org.last_active_user}</span>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );

    const renderActivityFeed = () => (
        <Card>
            <CardContent className="p-6">
                <div className="space-y-4">
                    {recentActivity.map(activity => (
                        <div 
                            key={activity.id}
                            className="flex items-start gap-3 pb-4 border-b last:border-0"
                        >
                            <div className="flex-shrink-0 mt-1">
                                {getActivityIcon(activity.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm text-gray-900">
                                    {formatActivityDescription(activity)}
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                    {new Date(activity.timestamp).toLocaleString()}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );

    const handleSystemDelete = async (userId, userName) => {
        if (!confirm(`Are you sure you want to completely delete ${userName} from the system? This will remove ALL their data including comments, responses, and organization memberships. This action CANNOT be undone.`)) {
            return;
        }

        try {
            const response = await fetch(`/api/admin/users/${userId}/system`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to delete user from system');
            }

            // Remove user from the local state
            setUsers(prev => prev.filter(user => user.id !== userId));
            setSuccess('User completely removed from system');
        } catch (error) {
            console.error('Error deleting user from system:', error);
            setError(error.message || 'Failed to delete user from system');
        }
    };

    if (error) {
        // Instead of returning a full-screen error, we'll continue to render the dashboard
        // and just show a small error banner at the top
        return (
            <div className="min-h-screen bg-gray-50 p-8">
                <div className="flex justify-between items-center mb-8">
                    <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
                </div>
                
                {/* Small error banner */}
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center">
                        <AlertTriangle className="mr-2 h-5 w-5 text-red-500" />
                        <div>
                            <p className="text-red-700 font-medium">
                                {error === 'Authentication required' 
                                    ? "Your session may have expired or you don't have admin privileges."
                                    : error}
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        {error === 'Authentication required' && (
                            <button 
                                onClick={() => navigate('/auth?redirect=/admin')} 
                                className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 text-sm rounded"
                            >
                                Log In Again
                            </button>
                        )}
                        <button 
                            onClick={() => {setError(null); fetchDashboardData();}}
                            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1 text-sm rounded"
                        >
                            Retry
                        </button>
                        <button 
                            onClick={() => setError(null)}
                            className="text-gray-500 hover:text-gray-700"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {renderNavigationTabs()}

                <Routes>
                    <Route index element={renderMetricsSection()} />
                    <Route path="organizations" element={<OrganizationDashboard />} />
                    <Route path="users" element={<UserManagement />} />
                    <Route path="brands" element={<BrandsManagement />} />
                    <Route path="brands-2/*" element={<SimpleBrandsList />} />
                    <Route path="*" element={<Navigate to="/admin" replace />} />
                </Routes>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg">Loading dashboard...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-8">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            </div>

            {renderNavigationTabs()}

            <Routes>
                <Route index element={renderMetricsSection()} />
                <Route path="users" element={<UserManagement />} />
                <Route path="organizations" element={
                    selectedOrganization ? (
                        <OrganizationDashboard
                            organization={selectedOrganization}
                            onBack={() => setSelectedOrganization(null)}
                        />
                    ) : (
                        renderOrganizationsTable()
                    )
                } />
                <Route path="brands/*" element={<BrandsManagement />} />
                <Route path="brands-2/*" element={<SimpleBrandsList />} />
                <Route path="*" element={<Navigate to="/admin" replace />} />
            </Routes>

            {showCreateModal && (
                <CreateOrganizationModal
                    onClose={() => setShowCreateModal(false)}
                    onSuccess={(org) => {
                        setSuccess('Organization created successfully');
                        setShowCreateModal(false);
                        fetchDashboardData();
                    }}
                />
            )}
        </div>
    );
};

export default AdminDashboard;